import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { profileApi } from '../services/api';

const SPORT_OPTIONS = [
  { value: '', label: 'All Sports' },
  { value: 'badminton', label: 'Badminton' },
  { value: 'tennis', label: 'Tennis' },
  { value: 'table_tennis', label: 'Table Tennis' },
  { value: 'squash', label: 'Squash' },
  { value: 'pickleball', label: 'Pickleball' },
  { value: 'padel', label: 'Padel' },
];

const sportLabel = (s) =>
  s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';

const parseSports = (raw) => {
  try { return Array.isArray(raw) ? raw : JSON.parse(raw || '[]'); }
  catch { return []; }
};

// ─── Rankings config ────────────────────────────────────────────────────────
// Each entry: { label, params } where params feeds directly into the API call.
// event_type: 'Singles' | 'Doubles'   (Mixed Doubles → event_type:'Doubles', gender:'Mixed')
// gender: "Men's" | "Women's" | "Boys'" | "Girls'" | 'Mixed'
// age_category: e.g. 'Masters 35+', 'U19', 'U17', …

const MASTERS_BANDS = ['35+', '40+', '45+', '50+', '55+', '60+', '65+', '70+'];

const mastersCats = (eventType) =>
  MASTERS_BANDS.flatMap(band => [
    { label: `${band} Men`,   params: { event_type: eventType, gender: "Men's",   age_category: `Masters ${band}` } },
    { label: `${band} Women`, params: { event_type: eventType, gender: "Women's", age_category: `Masters ${band}` } },
  ]);

const mastersMixedCats = () =>
  MASTERS_BANDS.map(band => ({
    label: band,
    params: { event_type: 'Doubles', gender: 'Mixed', age_category: `Masters ${band}` },
  }));

const BADMINTON_CONFIG = {
  ageGroups: ['Senior', 'Junior', 'Sub-Junior', 'Masters'],
  categories: {
    Masters: {
      Singles: mastersCats('Singles'),
      Doubles: mastersCats('Doubles'),
      Mixed:   mastersMixedCats(),
    },
    Senior: {
      Singles: [
        { label: 'Men',   params: { event_type: 'Singles', gender: "Men's"   } },
        { label: 'Women', params: { event_type: 'Singles', gender: "Women's" } },
      ],
      Doubles: [
        { label: 'Men',   params: { event_type: 'Doubles', gender: "Men's"   } },
        { label: 'Women', params: { event_type: 'Doubles', gender: "Women's" } },
      ],
      Mixed: [
        { label: 'Open', params: { event_type: 'Doubles', gender: 'Mixed' } },
      ],
    },
    Junior: {
      Singles: [
        { label: 'U19 Boys',  params: { event_type: 'Singles', gender: "Boys'",  age_category: 'U19' } },
        { label: 'U19 Girls', params: { event_type: 'Singles', gender: "Girls'", age_category: 'U19' } },
        { label: 'U17 Boys',  params: { event_type: 'Singles', gender: "Boys'",  age_category: 'U17' } },
        { label: 'U17 Girls', params: { event_type: 'Singles', gender: "Girls'", age_category: 'U17' } },
      ],
      Doubles: [
        { label: 'U19 Boys',  params: { event_type: 'Doubles', gender: "Boys'",  age_category: 'U19' } },
        { label: 'U19 Girls', params: { event_type: 'Doubles', gender: "Girls'", age_category: 'U19' } },
        { label: 'U17 Boys',  params: { event_type: 'Doubles', gender: "Boys'",  age_category: 'U17' } },
        { label: 'U17 Girls', params: { event_type: 'Doubles', gender: "Girls'", age_category: 'U17' } },
      ],
      Mixed: [
        { label: 'U19', params: { event_type: 'Doubles', gender: 'Mixed', age_category: 'U19' } },
        { label: 'U17', params: { event_type: 'Doubles', gender: 'Mixed', age_category: 'U17' } },
      ],
    },
    'Sub-Junior': {
      Singles: [
        { label: 'U15 Boys',  params: { event_type: 'Singles', gender: "Boys'",  age_category: 'U15' } },
        { label: 'U15 Girls', params: { event_type: 'Singles', gender: "Girls'", age_category: 'U15' } },
        { label: 'U13 Boys',  params: { event_type: 'Singles', gender: "Boys'",  age_category: 'U13' } },
        { label: 'U13 Girls', params: { event_type: 'Singles', gender: "Girls'", age_category: 'U13' } },
        { label: 'U11 Boys',  params: { event_type: 'Singles', gender: "Boys'",  age_category: 'U11' } },
        { label: 'U11 Girls', params: { event_type: 'Singles', gender: "Girls'", age_category: 'U11' } },
        { label: 'U9 Boys',   params: { event_type: 'Singles', gender: "Boys'",  age_category: 'U9'  } },
        { label: 'U9 Girls',  params: { event_type: 'Singles', gender: "Girls'", age_category: 'U9'  } },
      ],
      Doubles: [
        { label: 'U15 Boys',  params: { event_type: 'Doubles', gender: "Boys'",  age_category: 'U15' } },
        { label: 'U15 Girls', params: { event_type: 'Doubles', gender: "Girls'", age_category: 'U15' } },
        { label: 'U13 Boys',  params: { event_type: 'Doubles', gender: "Boys'",  age_category: 'U13' } },
        { label: 'U13 Girls', params: { event_type: 'Doubles', gender: "Girls'", age_category: 'U13' } },
        { label: 'U11 Boys',  params: { event_type: 'Doubles', gender: "Boys'",  age_category: 'U11' } },
        { label: 'U11 Girls', params: { event_type: 'Doubles', gender: "Girls'", age_category: 'U11' } },
        { label: 'U9 Boys',   params: { event_type: 'Doubles', gender: "Boys'",  age_category: 'U9'  } },
        { label: 'U9 Girls',  params: { event_type: 'Doubles', gender: "Girls'", age_category: 'U9'  } },
      ],
      Mixed: [
        { label: 'U15', params: { event_type: 'Doubles', gender: 'Mixed', age_category: 'U15' } },
        { label: 'U13', params: { event_type: 'Doubles', gender: 'Mixed', age_category: 'U13' } },
        { label: 'U11', params: { event_type: 'Doubles', gender: 'Mixed', age_category: 'U11' } },
        { label: 'U9',  params: { event_type: 'Doubles', gender: 'Mixed', age_category: 'U9'  } },
      ],
    },
  },
};

// Map sport value → config. Add other sports here when ready.
const SPORT_RANKINGS_CONFIG = {
  badminton: BADMINTON_CONFIG,
};

const FORMATS = ['Singles', 'Doubles', 'Mixed'];

// ─── Shared styles ───────────────────────────────────────────────────────────
const selectStyle = {
  height: 34, padding: '0 10px', fontSize: '0.82rem',
  border: '1px solid #e5e7eb', borderRadius: 8,
  background: 'white', color: '#374151', outline: 'none',
  cursor: 'pointer', fontFamily: 'inherit',
};

const Avatar = ({ name }) => (
  <div style={{
    width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg, var(--primary-color) 0%, #0D9488 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '1.1rem', fontWeight: '700', color: 'white',
  }}>
    {name?.charAt(0).toUpperCase() || '?'}
  </div>
);

// ─── Component ───────────────────────────────────────────────────────────────
const PlayersDirectory = () => {
  const [activeView, setActiveView] = useState('directory'); // 'directory' | 'rankings'

  // ── Directory state ──
  const [profiles, setProfiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState('');
  const [directorySport, setDirectorySport] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const debounceRef = useRef(null);

  // ── Rankings state ──
  const [rankings, setRankings] = useState([]);
  const [rankingsLoading, setRankingsLoading] = useState(false);
  const [rankingsSport, setRankingsSport] = useState('');      // required — nothing loads until set
  const [rankingsAgeGroup, setRankingsAgeGroup] = useState('Senior');
  const [rankingsFormat, setRankingsFormat] = useState('Singles');
  const [rankingsCategory, setRankingsCategory] = useState('Men'); // label string

  // Derived config for current sport
  const sportConfig = SPORT_RANKINGS_CONFIG[rankingsSport] || null;
  const currentCats = sportConfig?.categories[rankingsAgeGroup]?.[rankingsFormat] || [];

  // Always keep rankingsCategory in sync when the list changes
  const activeCat = currentCats.find(c => c.label === rankingsCategory) || currentCats[0] || null;

  // Build API params from the active category entry
  const buildApiParams = () => {
    if (!rankingsSport || !activeCat) return null;
    return { sport: rankingsSport, ...activeCat.params };
  };

  const rankingsTitle =
    `${rankingsAgeGroup} ${rankingsCategory} ${rankingsFormat === 'Mixed' ? 'Mixed Doubles' : rankingsFormat}`;

  // ── Handlers ──
  const handleSportChange = (sport) => {
    const cfg = SPORT_RANKINGS_CONFIG[sport];
    setRankingsSport(sport);
    setRankings([]);
    if (cfg) {
      const firstAge = cfg.ageGroups[0];
      const firstCat = cfg.categories[firstAge]?.Singles?.[0]?.label || '';
      setRankingsAgeGroup(firstAge);
      setRankingsFormat('Singles');
      setRankingsCategory(firstCat);
    }
  };

  const handleAgeGroupChange = (age) => {
    const cats = sportConfig?.categories[age]?.[rankingsFormat];
    setRankingsAgeGroup(age);
    setRankingsCategory(cats?.[0]?.label || '');
  };

  const handleFormatChange = (fmt) => {
    const cats = sportConfig?.categories[rankingsAgeGroup]?.[fmt];
    setRankingsFormat(fmt);
    setRankingsCategory(cats?.[0]?.label || '');
  };

  // ── Fetch rankings ──
  useEffect(() => {
    if (activeView !== 'rankings') return;
    const params = buildApiParams();
    if (!params) return;
    setRankingsLoading(true);
    profileApi.rankings(params)
      .then(res => setRankings(res.data))
      .catch(() => setRankings([]))
      .finally(() => setRankingsLoading(false));
  }, [activeView, rankingsSport, rankingsAgeGroup, rankingsFormat, rankingsCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Directory fetch ──
  const fetchProfiles = useCallback(async (q, off, append = false, sport = '') => {
    if (!append) setLoading(true); else setLoadingMore(true);
    try {
      const res = await profileApi.list(q, off, sport);
      const { profiles: rows, total: t } = res.data;
      setProfiles(prev => append ? [...prev, ...rows] : rows);
      setTotal(t);
      setHasMore(off + rows.length < t);
      setOffset(off + rows.length);
    } catch {
      // fail silently — empty state handles it
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => { fetchProfiles('', 0, false, directorySport); }, [fetchProfiles]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchProfiles(query, 0, false, directorySport);
    }, 280);
    return () => clearTimeout(debounceRef.current);
  }, [query, directorySport, fetchProfiles]);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="container" style={{ paddingTop: '1.5rem', paddingBottom: '3rem' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#111827', margin: '0 0 0.2rem' }}>Players</h1>
          <p style={{ color: '#9ca3af', fontSize: '0.8rem', margin: 0 }}>
            {activeView === 'directory' && !loading
              ? `${total.toLocaleString()} player${total !== 1 ? 's' : ''} registered on this platform`
              : activeView === 'rankings' ? 'Rankings are sport, event and category specific'
              : ''}
          </p>
        </div>
        {/* View toggle */}
        <div style={{ display: 'flex', gap: '3px', background: '#f3f4f6', padding: '3px', borderRadius: 8 }}>
          {[['directory', 'Directory'], ['rankings', 'Rankings']].map(([v, label]) => (
            <button
              key={v}
              onClick={() => setActiveView(v)}
              style={{
                padding: '5px 14px', fontSize: '0.8rem', fontWeight: 500, borderRadius: 6, border: 'none', cursor: 'pointer', transition: 'all 0.12s',
                background: activeView === v ? 'white' : 'transparent',
                color: activeView === v ? '#0D9488' : '#6b7280',
                boxShadow: activeView === v ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Rankings View ── */}
      {activeView === 'rankings' && (
        <div>

          {/* Sport dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', flexShrink: 0 }}>Sport</label>
            <select
              value={rankingsSport}
              onChange={e => handleSportChange(e.target.value)}
              style={{ ...selectStyle, height: 36, minWidth: 160 }}
            >
              <option value="">— Select sport —</option>
              {SPORT_OPTIONS.filter(o => o.value !== '').map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* No sport selected */}
          {!rankingsSport && (
            <div className="card">
              <div className="empty-state">
                <span className="empty-state-icon">🏆</span>
                <p>Select a sport above to view rankings.</p>
              </div>
            </div>
          )}

          {/* Sport selected but not yet configured */}
          {rankingsSport && !sportConfig && (
            <div className="card">
              <div className="empty-state">
                <span className="empty-state-icon">🚧</span>
                <p>Rankings for {SPORT_OPTIONS.find(o => o.value === rankingsSport)?.label} are coming soon.</p>
              </div>
            </div>
          )}

          {/* Full rankings navigation */}
          {rankingsSport && sportConfig && (
            <>
              {/* Age Group tabs */}
              <div style={{ display: 'flex', gap: '4px', background: '#f3f4f6', padding: '4px', borderRadius: 10, marginBottom: '0.75rem', width: 'fit-content' }}>
                {sportConfig.ageGroups.map(age => (
                  <button
                    key={age}
                    onClick={() => handleAgeGroupChange(age)}
                    style={{
                      padding: '6px 18px', fontSize: '0.85rem', fontWeight: 600, borderRadius: 7,
                      border: 'none', cursor: 'pointer', transition: 'all 0.12s',
                      background: rankingsAgeGroup === age ? 'white' : 'transparent',
                      color: rankingsAgeGroup === age ? '#0D9488' : '#6b7280',
                      boxShadow: rankingsAgeGroup === age ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                    }}
                  >
                    {age}
                  </button>
                ))}
              </div>

              {/* Format tabs */}
              <div style={{ display: 'flex', gap: '4px', marginBottom: '0.75rem', width: 'fit-content' }}>
                {FORMATS.map(fmt => (
                  <button
                    key={fmt}
                    onClick={() => handleFormatChange(fmt)}
                    style={{
                      padding: '5px 16px', fontSize: '0.8rem', fontWeight: 500, borderRadius: 6,
                      border: rankingsFormat === fmt ? '1.5px solid #0D9488' : '1.5px solid #e5e7eb',
                      cursor: 'pointer', transition: 'all 0.12s',
                      background: rankingsFormat === fmt ? '#F0FDFA' : 'white',
                      color: rankingsFormat === fmt ? '#0D9488' : '#6b7280',
                    }}
                  >
                    {fmt}
                  </button>
                ))}
              </div>

              {/* Category pills */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '1.25rem' }}>
                {currentCats.map(cat => (
                  <button
                    key={cat.label}
                    onClick={() => setRankingsCategory(cat.label)}
                    style={{
                      padding: '4px 14px', fontSize: '0.78rem', fontWeight: 500, borderRadius: 999,
                      border: rankingsCategory === cat.label ? '1.5px solid #0D9488' : '1.5px solid #e5e7eb',
                      cursor: 'pointer', transition: 'all 0.1s',
                      background: rankingsCategory === cat.label ? '#F0FDFA' : 'white',
                      color: rankingsCategory === cat.label ? '#0D9488' : '#6b7280',
                    }}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              {/* Section title */}
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: '0.6rem', letterSpacing: '0.01em' }}>
                {rankingsTitle} Rankings
              </div>

              {/* Table or states */}
              {rankingsLoading ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem 1rem', background: 'white', border: '1px solid #f0f0f0', borderRadius: 10 }}>
                      <div className="skeleton" style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0 }} />
                      <div className="skeleton" style={{ height: 14, width: '30%' }} />
                      <div className="skeleton" style={{ height: 14, width: '12%', marginLeft: 'auto' }} />
                      <div className="skeleton" style={{ height: 14, width: '12%' }} />
                      <div className="skeleton" style={{ height: 14, width: '10%' }} />
                    </div>
                  ))}
                </div>
              ) : rankings.length === 0 ? (
                <div className="card">
                  <div className="empty-state">
                    <span className="empty-state-icon">🏆</span>
                    <p>No {rankingsTitle.toLowerCase()} data yet. Rankings appear once players have completed matches.</p>
                  </div>
                </div>
              ) : (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <table className="table" style={{ margin: 0 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 48, textAlign: 'center' }}>#</th>
                        <th>Player</th>
                        <th style={{ textAlign: 'center' }}>Tournaments</th>
                        <th style={{ textAlign: 'center' }}>Played</th>
                        <th style={{ textAlign: 'center' }}>Won</th>
                        <th style={{ textAlign: 'center' }}>Win %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rankings.map((r, i) => {
                        const location = [r.city, r.state, r.country].filter(Boolean).join(', ');
                        const isTop3 = i < 3;
                        const medals = ['🥇', '🥈', '🥉'];
                        return (
                          <tr key={r.id} style={{ background: i === 0 ? '#fffbeb' : 'white' }}>
                            <td style={{ textAlign: 'center', fontWeight: 700, fontSize: isTop3 ? '1rem' : '0.85rem', color: isTop3 ? '#b45309' : '#9ca3af' }}>
                              {isTop3 ? medals[i] : i + 1}
                            </td>
                            <td>
                              <Link to={`/profiles/${r.id}`} style={{ textDecoration: 'none' }}>
                                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#111827' }}>{r.display_name}</div>
                                {location && <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{location}</div>}
                              </Link>
                            </td>
                            <td style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.85rem' }}>{r.tournaments_played}</td>
                            <td style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.85rem' }}>{r.matches_played}</td>
                            <td style={{ textAlign: 'center', fontWeight: 600, color: '#059669', fontSize: '0.9rem' }}>{r.matches_won}</td>
                            <td style={{ textAlign: 'center', fontSize: '0.85rem' }}>
                              <span style={{
                                display: 'inline-block', padding: '2px 7px', borderRadius: 999, fontWeight: 600, fontSize: '0.78rem',
                                background: r.win_pct >= 70 ? '#d1fae5' : r.win_pct >= 50 ? '#eff6ff' : '#f3f4f6',
                                color: r.win_pct >= 70 ? '#065f46' : r.win_pct >= 50 ? '#1d4ed8' : '#6b7280',
                              }}>
                                {r.win_pct ?? 0}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Directory View ── */}
      {activeView === 'directory' && (
        <>
          {/* Filters row */}
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap' }}>

            {/* Search */}
            <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 400 }}>
              <svg
                width="14" height="14" viewBox="0 0 20 20" fill="none"
                style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }}
              >
                <path d="M17 17l-3.5-3.5M14.5 8.5a6 6 0 11-12 0 6 6 0 0112 0z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
              <input
                type="text"
                placeholder="Search by name or phone..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                style={{
                  width: '100%', height: 36, paddingLeft: 32, paddingRight: query ? 32 : 10,
                  fontSize: '0.85rem', border: '1px solid #e5e7eb', borderRadius: 8,
                  background: 'white', color: '#111827', outline: 'none', fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
                onFocus={e => { e.target.style.borderColor = '#5EEAD4'; e.target.style.boxShadow = '0 0 0 3px rgba(13,148,136,0.12)'; }}
                onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '1rem', lineHeight: 1, padding: 0 }}
                >
                  ×
                </button>
              )}
            </div>

            {/* Sport filter */}
            <select
              value={directorySport}
              onChange={e => setDirectorySport(e.target.value)}
              style={{ height: 36, padding: '0 10px', fontSize: '0.82rem', border: '1px solid #e5e7eb', borderRadius: 8, background: 'white', color: '#374151', outline: 'none', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}
            >
              {SPORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Grid */}
          {loading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : profiles.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <span className="empty-state-icon">👤</span>
                <p>{query ? `No players found for "${query}"` : 'No players registered yet.'}</p>
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.75rem' }}>
                {profiles.map(p => {
                  const sports = parseSports(p.sports);
                  const location = [p.city, p.state, p.country].filter(Boolean).join(', ');
                  return (
                    <Link key={p.id} to={`/profiles/${p.id}`} style={{ textDecoration: 'none' }}>
                      <div
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.75rem',
                          padding: '0.75rem 1rem',
                          background: 'white', border: '1px solid #f0f0f0', borderRadius: 12,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                          transition: 'box-shadow 0.15s, border-color 0.15s',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(13,148,136,0.1)'; e.currentTarget.style.borderColor = '#99F6E4'; }}
                        onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#f0f0f0'; }}
                      >
                        <Avatar name={p.display_name} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: '600', fontSize: '0.92rem', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {p.display_name}
                          </div>
                          {(location || p.nationality) && (
                            <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {location || p.nationality}
                            </div>
                          )}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.35rem', alignItems: 'center' }}>
                            {sports.slice(0, 2).map(s => (
                              <span key={s} style={{ fontSize: '0.62rem', padding: '1px 6px', background: '#d1fae5', color: '#065f46', borderRadius: 999, border: '1px solid #6ee7b7' }}>
                                {sportLabel(s)}
                              </span>
                            ))}
                            {Number(p.tournaments_played) > 0 && (
                              <span style={{ fontSize: '0.62rem', color: '#9ca3af', marginLeft: 'auto' }}>
                                {p.tournaments_played} tournament{p.tournaments_played !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>

              {/* Load more */}
              {hasMore && (
                <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                  <button
                    className="btn btn-outline"
                    onClick={() => fetchProfiles(query, offset, true, directorySport)}
                    disabled={loadingMore}
                  >
                    {loadingMore ? 'Loading…' : `Load more (${total - offset} remaining)`}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default PlayersDirectory;
